import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { api } from '../../../config';
import { getAuthToken } from '../../../auth/tokenvalidation';
import {
  getOwnClauseResponse,
  hasClauseResponse,
  matchesRespondMitra,
  normalizeRespondOperationalToleranceValidity,
  sortRespondOperationalToleranceValidities,
} from './helpers';
import { buildRespondTypeResponseListUrl } from './listParams';
import { unwrapPayload, unwrapListItems } from '../../api/unwrap';

const parseResponseItems = payload => {
  const list = unwrapListItems(payload)
    .map(normalizeRespondOperationalToleranceValidity)
    .filter(Boolean);

  if (list.length > 0) return list;

  const single = normalizeRespondOperationalToleranceValidity(
    unwrapPayload(payload)
  );
  return single ? [single] : [];
};

const buildItemUrl = id =>
  `${api.API_URL}/respond-operational-tolerance-validity-document/${id}`;

const groupOwnResponses = (items, procurementMitraId = null) => {
  const scoped = (items ?? []).filter(item =>
    matchesRespondMitra(item, procurementMitraId, { allowUnscoped: true })
  );
  const byType = {};

  scoped.forEach(item => {
    const key = Number(item.typeResponse);
    if (!Number.isFinite(key)) return;
    if (!byType[key]) byType[key] = [];
    byType[key].push(item);
  });

  return Object.entries(byType).reduce((acc, [key, list]) => {
    acc[Number(key)] = getOwnClauseResponse(
      sortRespondOperationalToleranceValidities(list),
      { isEvaluator: false }
    );
    return acc;
  }, {});
};

const useRespondTypeResponse = (
  authToken = null,
  procurementId = null,
  procurementMitraId = null
) => {
  const [responses, setResponses] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const fetchSeqRef = useRef(0);

  const fetchResponses = useCallback(async () => {
    const token = authToken || getAuthToken();
    if (!token || !procurementId) {
      setResponses({});
      return;
    }

    if (!procurementMitraId) {
      setResponses({});
      return;
    }

    const seq = fetchSeqRef.current + 1;
    fetchSeqRef.current = seq;
    setIsLoading(true);
    try {
      const result = await axios.get(
        buildRespondTypeResponseListUrl({
          procurementId,
          procurementMitraId,
        }),
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
          },
        }
      );
      if (fetchSeqRef.current !== seq) return;
      setResponses(
        groupOwnResponses(parseResponseItems(result), procurementMitraId)
      );
    } catch (error) {
      if (fetchSeqRef.current !== seq) return;
      if (error?.response?.status === 404) {
        setResponses({});
        return;
      }
      setResponses({});
    } finally {
      if (fetchSeqRef.current === seq) setIsLoading(false);
    }
  }, [authToken, procurementId, procurementMitraId]);

  useEffect(() => {
    fetchResponses();
  }, [fetchResponses]);

  const saveResponse = useCallback(
    async (typeResponse, respondNotes) => {
      const token = authToken || getAuthToken();
      const notes = String(respondNotes ?? '').trim();
      if (
        !token ||
        !procurementId ||
        !typeResponse ||
        !hasClauseResponse(notes)
      ) {
        return null;
      }

      const existing = responses[Number(typeResponse)];
      const payload = {
        procurementId,
        respondNotes: notes,
        typeResponse: Number(typeResponse),
      };

      setIsSaving(true);
      try {
        const result = existing?.id
          ? await axios.put(buildItemUrl(existing.id), payload, {
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
            })
          : await axios.post(
              `${api.API_URL}/respond-operational-tolerance-validity-document`,
              payload,
              {
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${token}`,
                },
              }
            );

        const saved = normalizeRespondOperationalToleranceValidity(
          unwrapPayload(result)
        );

        if (saved) {
          setResponses(prev => ({
            ...prev,
            [saved.typeResponse]: saved,
          }));
          return saved;
        }

        await fetchResponses();
        return null;
      } catch (error) {
        if (error?.response?.status === 409) {
          await fetchResponses();
        }
        throw error;
      } finally {
        setIsSaving(false);
      }
    },
    [authToken, fetchResponses, procurementId, responses]
  );

  const deleteResponse = useCallback(
    async typeResponse => {
      const token = authToken || getAuthToken();
      const existing = responses[Number(typeResponse)];
      if (!token || !existing?.id) {
        return;
      }

      setIsSaving(true);
      try {
        await axios.delete(buildItemUrl(existing.id), {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setResponses(prev => {
          const next = { ...prev };
          delete next[Number(typeResponse)];
          return next;
        });
      } catch (error) {
        if (error?.response?.status === 409) {
          await fetchResponses();
        }
        throw error;
      } finally {
        setIsSaving(false);
      }
    },
    [authToken, fetchResponses, responses]
  );

  return {
    responses,
    isLoading,
    isSaving,
    refresh: fetchResponses,
    saveResponse,
    deleteResponse,
  };
};

export default useRespondTypeResponse;
